import { useFormik } from 'formik';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { deriveCouponErrorMessage } from '@hooks/useApplyCoupon/error';
import { getCouponDisplayName } from '@hooks/useShoppingCart/coupon';
import { usePromoLearnMoreUrl } from '@hooks/usePromoLearnMoreUrl';
import { useShoppingCart } from '@hooks/useShoppingCart/useShoppingCart';
import { useSessionContext } from '@contexts/SessionContext';
import LoadingButton from '@shared/components/buttons/LoadingButton/LoadingButton';
import FormErrorMessage from '@shared/components/feedback/FormErrorMessage/FormErrorMessage';
import { couponSchema } from './CouponSection.schema';

const TITLE_ID = 'coupon-section-title';

export const CouponSection = () => {
  const promoLearnMoreUrl = usePromoLearnMoreUrl();
  const { sessionId } = useSessionContext();
  const { appliedCoupon, isApplyingCoupon, isRemovingCoupon, applyCoupon, removeCoupon } = useShoppingCart(
    sessionId ?? ''
  );

  const formik = useFormik({
    initialValues: { code: '' },
    validationSchema: couponSchema,
    onSubmit: (values, helpers) => {
      const code = values.code.trim().toUpperCase();
      if (!code) {
        helpers.setFieldError('code', 'Enter a valid promo code');
        return;
      }
      applyCoupon(code, {
        onSuccess: () => helpers.resetForm(),
        onError: (err) => helpers.setFieldError('code', deriveCouponErrorMessage(err, code)),
      });
    },
  });

  const inlineError = formik.touched.code && formik.errors.code ? formik.errors.code : '';

  return (
    <Stack spacing={1.25} useFlexGap>
      <Typography id={TITLE_ID} variant="heading.detail" className="CouponSection-title" gutterBottom>
        Apply Promotional Code
      </Typography>

      <form onSubmit={formik.handleSubmit} noValidate>
        <Stack direction="row" alignItems="center" spacing={1} useFlexGap>
          <TextField
            fullWidth
            placeholder="Enter promo code"
            {...formik.getFieldProps('code')}
            inputProps={{ 'aria-labelledby': TITLE_ID, 'aria-describedby': 'coupon-error' }}
            error={formik.touched.code && !!formik.errors.code}
          />
          <LoadingButton
            type="submit"
            variant="contained"
            loading={isApplyingCoupon}
            aria-label="APPLY"
            sx={{ minHeight: '40px', maxHeight: '40px' }}
          >
            APPLY
          </LoadingButton>
        </Stack>
        {inlineError && (
          <Box id="coupon-error" sx={{ mt: '4px' }}>
            <FormErrorMessage>{inlineError}</FormErrorMessage>
          </Box>
        )}
      </form>

      {appliedCoupon && (
        <>
          <Paper variant="couponApplied" elevation={0}>
            <Typography variant="label.button" className="CouponSection-appliedCode">
              {getCouponDisplayName(appliedCoupon)}
            </Typography>
            <LoadingButton
              variant="text"
              loading={isRemovingCoupon}
              onClick={() => removeCoupon()}
              aria-label="Remove"
              sx={{ textTransform: 'none' }}
            >
              <Typography variant="label.field" className="CouponSection-removeLabel">
                Remove
              </Typography>
            </LoadingButton>
          </Paper>
          <Typography variant="body.sm" className="CouponSection-learnMore">
            <Link
              variant="couponLearnMore"
              href={promoLearnMoreUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Learn more about the promotion (opens in new tab)"
            >
              <Typography variant="label.fieldSmall" className="CouponSection-learnMoreLink" component="span">
                Click Here
              </Typography>
            </Link>
            {` to learn more about the promotion.`}
          </Typography>
        </>
      )}
    </Stack>
  );
};
